<template>
  <div class="file-input-container">
    <label :for="'file-input-' + _uid" v-if="showLabels">
      {{ label }}
      <span v-if="isRequired" class="text-danger">*</span>
    </label>
    <!-- If fileId exists, show 'View File' and 'Change File' -->
    <div v-if="typeof value == 'string' && value.trim() !== ''">
      <p class="mb-1">
        File already uploaded:
        <a @click="viewFile" class="text-primary" style="cursor: pointer">
          View File
        </a>
      </p>
    </div>
    <div v-if="showChangeFileButton" class="button-group">
      <button
        type="button"
        class="btn btn-cp choose-file-btn"
        @click="clearFile"
      >
        Change File
      </button>
      <button
        type="button"
        class="btn btn-danger choose-file-btn"
        @click="resetFile"
      >
        Clear File
      </button>
    </div>
    <div
      class="file-input-wrapper"
      v-else
      @click="triggerFileInput"
      style="cursor: pointer"
    >
      <input
        :id="'file-input-' + _uid"
        type="file"
        class="form-control file-input"
        @change="handleFileInput"
        :disabled="disabled"
      />
      <button
        type="button"
        class="btn btn-cp choose-file-btn"
        @click.stop="triggerFileInput"
      >
        Choose File
      </button>
      <span class="file-name">
        {{ fileName }}
      </span>
      <button
        v-if="fileName"
        type="button"
        class="btn btn-secondary choose-file-btn ms-2"
        @click.stop="resetFile"
      >
        <i class="fa fa-times-circle" aria-hidden="true"></i>
      </button>
    </div>
    <span style="font-size: 12px" v-if="allowedFileTypes"
      >only {{ allowedFileTypes }} files are allowed</span
    >
    <template v-if="showErrors">
      <p class="text-danger mb-1" v-for="(error, idx) in errorList" :key="idx">
        <b-icon-exclamation-circle></b-icon-exclamation-circle>
        <span class="ml-1">{{ error }}</span>
      </p>
    </template>
    <!-- PDF Modal -->
    <file-view-modal
      v-if="modalUrl"
      :url.sync="modalUrl"
      :key="modalUrl"
      :axios="applicantAxios"
      :modalId="modalId"
      :title="label"
    />
  </div>
</template>

<script>
import validators from "../../_utils/validators";
import FileViewModal from "../../../Common/_components/file-view-modal.vue";
import config from "../../../../config";
import applicantAxios from "../../../ApplicantMaster/_utils/axios";

export default {
  name: "FileInputRenderer",
  props: {
    label: String,
    properties: Object,
    useValidation: Boolean,
    validators: Array,
    disabled: { type: Boolean, default: false },
    showLabels: { type: Boolean, default: true },
    showErrors: { type: Boolean, default: true },
    type: [String, File, FileList],
    value: {
      type: [String, File, FileList],
      default: null,
    },
  },
  mounted() {
    this.showChangeFileButton =
      typeof this.value == "string" && this.value.trim() !== "" ? true : false;
  },
  data() {
    return {
      fileName: "",
      errorList: [],
      config,
      applicantAxios,
      modalUrl: null,
      modalId: `file-modal-${this._uid}`,
      showChangeFileButton: false,
    };
  },
  computed: {
    isRequired() {
      return (
        this.useValidation &&
        this.validators?.some((e) => e.validator === "required")
      );
    },
    isPreloadedFile() {
      return typeof this.value === "string" && this.value.trim() !== "";
    },
    fileUrl() {
      if (!this.value) return null;
      return `${
        this.config.ROOT_API
      }/applicant-master/joining-form/files/?FileId=${encodeURIComponent(
        this.value
      )}`;
    },
    allowedFileTypes() {
      const validator = this.validators.find((v) => v.validator == "fileType");
      let ans = "";
      if (validator && validator.allowedTypes)
        ans = validator.allowedTypes.join(", ");
      if (validator && validator.allowedTypes.includes("jpeg")) ans += ", jpg";
      return ans;
    },
  },
  watch: {
    modalUrl(newUrl, oldUrl) {
      if (newUrl !== oldUrl) {
        this.$nextTick(() => {
          const modalId = `#${this.modalId}`;
          $(modalId).modal("show");
          // Clean up modal on hide
          $(modalId).on("hidden.bs.modal", () => {
            this.modalUrl = null;
          });
        });
      }
    },
  },

  methods: {
    triggerFileInput() {
      this.$el.querySelector(".file-input").click();
    },
    handleFileInput(event) {
      const files = event.target.files;
      if (!files || files.length === 0) {
        return;
      }
      this.fileName = files.length
        ? Array.from(files)
            .map((f) => f.name)
            .join(", ")
        : "";

      // this.value = null;

      if (this.useValidation) this.checkError(files);
      this.$emit("fileSelected", files); // optional
      this.$emit("input", files);
    },
    clearFile() {
      this.fileName = "";
      this.showChangeFileButton = false;
    },
    viewFile() {
      this.modalUrl = this.fileUrl + "&t=" + Date.now();
      this.$nextTick(() => {
        const sel = `#${this.modalId}`;
        $(sel).modal("hide").modal("show");
      });
    },
    resetFile() {
      this.fileName = "";
      this.showChangeFileButton = false;
      this.errorList = [];
      this.$emit("input", null);
      const fileInput = this.$el.querySelector(".file-input");
      if (fileInput) fileInput.value = ""; // reset file input
    },
    checkError(files, showError = true) {
      const errors = [];
      const myDefaultValidators = [
        // { validator: "required" },
        { validator: "fileType", allowedTypes: ["jpeg", "png", "pdf"] },
        { validator: "fileSize", maxSize: 8 },
      ];
      for (const validator of myDefaultValidators) {
        const validatorIndex = this.validators.findIndex(
          (v) => v.validator == validator.validator
        );
        let err = null;
        if (validatorIndex == -1) {
          err = validators[validator.validator](files, validator);
        } else {
          const v = this.validators[validatorIndex];
          err = validators[v.validator](files, v);
        }
        if (err) errors.push(err);
      }
      const requiredValidator = this.validators?.find(
        (v) => v.validator === "required"
      );
      if (requiredValidator) {
        const error = validators[requiredValidator.validator](
          files,
          requiredValidator
        );
        if (error) errors.push(error);
      }
      if (showError) this.errorList = errors;
      if (errors.length) {
        this.$emit("error", {
          key: this.key,
          errors,
        });
        return true;
      }
      return false;
    },
  },
  components: {
    FileViewModal,
  },
};
</script>

<style scoped>
.file-input-container {
  display: flex;
  flex-direction: column;
}

.file-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #ced4da;
  border-radius: 5px;
  background: #fff;
  width: 100%;
  padding: 7px 0px 8px;
  height: 2rem;
  box-sizing: border-box;
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.choose-file-btn {
  border-radius: 4px;
  font-size: 13px;
  padding: 5px 8px;
  height: 1.94rem;
}

.file-name {
  flex: 1;
  margin-left: 10px;
  color: #6c757d;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-style: italic;
}

.button-group {
  display: flex;
  gap: 10px; /* Adjust this value to control spacing */
}
</style>
